import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableItem } from './SortableItem';

export function RankingQuestion({ question, answers, setAnswers }) {
  const allOptions = question.options;
  const ranking = answers[question.key] || [];
  const available = allOptions.filter(opt => !ranking.includes(opt));

  // Setup sensors for mouse, touch, and keyboard
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Mindestbewegung von 8px vor Aktivierung
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100, // Kürzere Verzögerung für bessere Reaktion
        tolerance: 8, // Kleine Bewegung erlaubt
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = ranking.findIndex(item => item === active.id);
      const newIndex = ranking.findIndex(item => item === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newRanking = arrayMove(ranking, oldIndex, newIndex);
        setAnswers(a => ({ ...a, [question.key]: newRanking }));
      }
    }
  }

  const addToRanking = (option) => {
    setAnswers(a => ({
      ...a,
      [question.key]: [...ranking, option]
    }));
  };

  const removeFromRanking = (option) => {
    setAnswers(a => ({
      ...a,
      [question.key]: ranking.filter(o => o !== option)
    }));
  };

  return (
    <div className="ranking-container" style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
      {/* Available Options */}
      <div className="ranking-available" style={{ flex: 1, minWidth: 300 }}>
        <div style={{ 
          fontWeight: 700, 
          marginBottom: 16, 
          color: "#333",
          fontSize: "1.1rem",
          borderBottom: "2px solid #000000",
          paddingBottom: 8
        }}>
          📋 Verfügbare Optionen
        </div>
        <ul className="ranking-available-list" style={{ padding: 0, margin: 0, listStyle: "none" }}>
          {available.map(opt => (
            <li key={opt} className="ranking-available-item" style={{ 
              marginBottom: 12, 
              background: "linear-gradient(135deg, #f8f7ff 0%, #ebe8ff 100%)", 
              borderRadius: 12, 
              padding: "14px 16px",
              border: "1px solid #e0d9ff",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 4px rgba(124, 77, 255, 0.1)"
            }}>
              <div className="ranking-available-item-inner" style={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "space-between" 
              }}>
                <span style={{ 
                  flex: 1, 
                  fontSize: "0.95rem",
                  lineHeight: 1.4,
                  color: "#444"
                }}>
                  {opt}
                </span>
                <button
                  className="ranking-add-btn"
                  style={{
                    border: "none",
                    background: "#000000",
                    color: "#fff",
                    borderRadius: 6,
                    padding: "4px 8px",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 12,
                    transition: "all 0.2s ease",
                    boxShadow: "0 2px 4px rgba(124, 77, 255, 0.3)",
                    minWidth: "32px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0
                  }}
                  onClick={() => addToRanking(opt)}
                  onMouseOver={(e) => {
                    e.target.style.transform = "scale(1.1)";
                    e.target.style.boxShadow = "0 3px 8px rgba(124, 77, 255, 0.4)";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = "scale(1)";
                    e.target.style.boxShadow = "0 2px 4px rgba(124, 77, 255, 0.3)";
                  }}
                >
                  ►
                </button>
              </div>
            </li>
          ))}
        </ul>
        {available.length === 0 && (
          <div style={{ 
            color: "#999", 
            fontStyle: "italic", 
            textAlign: "center",
            padding: "20px",
            background: "#f9f9f9",
            borderRadius: 12,
            border: "2px dashed #ddd"
          }}>
            Alle Optionen wurden zur Reihenfolge hinzugefügt! 🎉
          </div>
        )}
      </div>
      
      {/* Ranking Area */}
      <div className="ranking-area" style={{ flex: 2, minWidth: 400 }}>
        <div style={{ 
          fontWeight: 700, 
          marginBottom: 16, 
          color: "#333",
          fontSize: "1.1rem",
          borderBottom: "2px solid #000000",
          paddingBottom: 8
        }}>
          🏆 Deine Reihenfolge (1 = bevorzugt)
        </div>
        <DndContext 
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={ranking} strategy={verticalListSortingStrategy}>
            <div className="ranking-list" style={{ 
              display: "flex", 
              flexDirection: "column", 
              gap: 12, 
              minHeight: 120,
              background: ranking.length === 0 ? "#fafafa" : "transparent",
              borderRadius: 12,
              padding: ranking.length === 0 ? "24px" : "8px",
              border: ranking.length === 0 ? "2px dashed #ccc" : "none"
            }}>
              {ranking.length === 0 && (
                <div style={{ 
                  color: "#999", 
                  fontStyle: "italic",
                  textAlign: "center",
                  fontSize: "1rem"
                }}>
                  💡 Klicke auf ► oder ziehe Optionen hierher, um deine Reihenfolge zu bestimmen
                </div>
              )}
              {ranking.map((opt, idx) => (
                <SortableItem 
                  key={opt} 
                  id={opt} 
                  option={opt} 
                  index={idx}
                  onRemove={() => removeFromRanking(opt)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}